<template>
  <!-- Fechas clave del proyecto (Datos de contrato): fechas contractuales y previstas de
       instalación (manuales, con motivo e historial) + inicio/fin reales derivados de las
       actividades (Planificación). Cada cambio de una fecha manual con valor previo queda
       registrado en el "Historial de revisiones contractuales". -->
  <summary-card :title="$t('prm_summary_kd_project')" :mode="mode">
    <!-- Fechas clave del proyecto -->
    <div class="kd-sub">{{ $t('prm_summary_kd_project') }}</div>
    <div v-for="f in projectRows" :key="f.field" class="kv" :class="rowCls(f)">
      <span class="lbl">{{ $t(f.label) }}</span>
      <span class="val">
        <template v-if="f.manual">
          <prm-date-revision-field :value="manualVal(f.field)" @save="onSave(f.field, $event)"></prm-date-revision-field>
          <source-chip v-if="mode" source="cm"></source-chip>
        </template>
        <template v-else>
          <b>{{ autoVal(f.field) }}</b>
          <source-chip v-if="mode" source="planif"></source-chip>
        </template>
      </span>
    </div>

    <!-- Historial de revisiones contractuales -->
    <div class="kd-sub">{{ $t('prm_summary_kd_history') }}</div>
    <div v-if="!revisions.length" class="kd-empty">{{ $t('prm_summary_kd_history_empty') }}</div>
    <div v-else class="kd-rev-list">
      <div v-for="(r, i) in sortedRevisions" :key="'r' + i" class="kd-rev">
        <!-- Edición de una revisión (para corregir o borrar un registro metido por error). -->
        <template v-if="editingRev === r">
          <b class="kd-rev-field">{{ $t(labelOf(r.field)) }}</b>
          <input v-model="revDate" type="text" class="kd-rev-in date" placeholder="dd/mm/aaaa">
          <input v-model="revReason" type="text" class="kd-rev-in reason" :placeholder="$t('prm_summary_kd_reason_ph')">
          <button type="button" class="kd-rev-btn save" @click="saveRev(r)">{{ $t('prm_summary_kd_save') }}</button>
          <button type="button" class="kd-rev-btn cancel" @click="editingRev = null">{{ $t('prm_summary_kd_cancel') }}</button>
        </template>
        <template v-else>
          <b class="kd-rev-field">{{ $t(labelOf(r.field)) }}</b>
          <span class="kd-rev-old">{{ fmtDate(r.oldValue) }}</span>
          <span class="kd-rev-when">{{ fmtDateTime(r.changedAt) }}</span>
          <span v-if="r.reason" class="kd-rev-reason">· {{ r.reason }}</span>
          <button type="button" class="kd-rev-ic" v-tip="$t('prm_summary_s03_edit')" @click="editRev(r)"><b-icon pack="fas" icon="pen" size="is-small"></b-icon></button>
          <button type="button" class="kd-rev-ic rm" v-tip="$t('prm_summary_remove_row')" @click="deleteRev(r)"><b-icon pack="fas" icon="trash-can" size="is-small"></b-icon></button>
        </template>
      </div>
    </div>

    <!-- Fechas clave de la instalación (Field) -->
    <div class="kd-sub">{{ $t('prm_summary_kd_installation') }}<span class="kd-note">{{ $t('prm_summary_kd_installation_note') }}</span></div>
    <div v-for="f in installRows" :key="f.field" class="kv" :class="rowCls(f)">
      <span class="lbl">{{ $t(f.label) }}</span>
      <span class="val">
        <template v-if="f.manual">
          <prm-date-revision-field :value="manualVal(f.field)" @save="onSave(f.field, $event)"></prm-date-revision-field>
          <source-chip v-if="mode" source="cm"></source-chip>
        </template>
        <template v-else>
          <b>{{ autoVal(f.field) }}</b>
          <source-chip v-if="mode" source="planif"></source-chip>
        </template>
      </span>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import SourceChip from './source-chip.vue'
  import PrmDateRevisionField from './date-revision-field.vue'
  import { fmtDate, fmtDateTime } from '../shared/format'
  import { tip } from '../shared/tip'

  // Definición de filas: field (clave en s02.keyDates o sources.keyDates), label i18n y si es manual.
  const PROJECT_ROWS = [
    { field: 'contractStart', label: 'prm_summary_kd_contract_start', manual: true },
    { field: 'contractEnd', label: 'prm_summary_kd_contract_end', manual: true },
    { field: 'realStart', label: 'prm_summary_kd_real_start', manual: false },
    { field: 'realEnd', label: 'prm_summary_kd_real_end', manual: false },
    { field: 'contractSignature', label: 'prm_summary_kd_signature', manual: true },
  ]
  const INSTALL_ROWS = [
    { field: 'installPlannedStart', label: 'prm_summary_kd_install_planned_start', manual: true },
    { field: 'installPlannedEnd', label: 'prm_summary_kd_install_planned_end', manual: true },
    { field: 'installRealStart', label: 'prm_summary_kd_install_real_start', manual: false },
    { field: 'installRealEnd', label: 'prm_summary_kd_install_real_end', manual: false },
  ]

  export default {
    name: 'PrmSummaryS02KeyDates',

    components: { SummaryCard, SourceChip, PrmDateRevisionField },

    directives: { tip },

    props: {
      keyDates: { type: Object, default: () => ({}) },   // fechas manuales (doc.s02.keyDates)
      autoDates: { type: Object, default: () => ({}) },  // fechas reales de actividades (sources.keyDates)
      revisions: { type: Array, default: () => [] },     // historial (doc.s02.dateRevisions, reciente primero)
      mode: { type: Boolean, default: false },
    },

    data () {
      // Edición en línea de una revisión del historial (objeto en edición + borradores).
      return { editingRev: null, revDate: '', revReason: '' }
    },

    computed: {
      projectRows () { return PROJECT_ROWS },
      installRows () { return INSTALL_ROWS },
      // Historial siempre con los más recientes arriba (por changedAt, ISO ordenable).
      sortedRevisions () {
        return [...this.revisions].sort((a, b) => String(b.changedAt || '').localeCompare(String(a.changedAt || '')))
      },
    },

    methods: {
      fmtDate,
      fmtDateTime,
      manualVal (field) { return this.keyDates[field] || '' },
      autoVal (field) { return this.autoDates[field] ? fmtDate(this.autoDates[field]) : '—' },
      rowCls (f) { return { manual: this.mode && f.manual, sourced: this.mode && !f.manual } },
      labelOf (field) {
        const row = [...PROJECT_ROWS, ...INSTALL_ROWS].find((r) => r.field === field)
        return row ? row.label : field
      },
      // Guarda la fecha manual y, si tenía un valor previo distinto, registra la revisión.
      onSave (field, { value, reason }) {
        const old = this.keyDates[field] || ''
        this.$emit('save-field', { path: 's02.keyDates.' + field, value: value || null })
        if (old && value !== old) {
          const rev = { field, oldValue: old, reason: reason || '', changedAt: new Date().toISOString() }
          this.$emit('save-list', { path: 's02.dateRevisions', items: [rev, ...this.revisions] })
        }
      },

      // Edición de una revisión existente: la fecha se edita en dd/mm/aaaa y se guarda en ISO.
      editRev (r) {
        this.editingRev = r
        this.revDate = r.oldValue ? fmtDate(r.oldValue) : ''
        this.revReason = r.reason || ''
      },
      saveRev (r) {
        const iso = this.revToIso(this.revDate)
        if ((this.revDate || '').trim() !== '' && iso === null) return // fecha inválida: no cerramos
        const items = this.revisions.map((x) => (x === r ? { ...x, oldValue: iso, reason: (this.revReason || '').trim() } : x))
        this.$emit('save-list', { path: 's02.dateRevisions', items })
        this.editingRev = null
      },
      deleteRev (r) {
        const items = this.revisions.filter((x) => x !== r)
        this.$emit('save-list', { path: 's02.dateRevisions', items })
        if (this.editingRev === r) this.editingRev = null
      },
      // dd/mm/aaaa -> ISO (YYYY-MM-DD); null si no es una fecha válida.
      revToIso (s) {
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s || '').trim())
        if (!m) return null
        const d = m[1].padStart(2, '0'), mo = m[2].padStart(2, '0'), y = m[3]
        const dt = new Date(y + '-' + mo + '-' + d + 'T00:00:00')
        if (isNaN(dt.getTime()) || dt.getMonth() + 1 !== Number(mo) || dt.getDate() !== Number(d)) return null
        return y + '-' + mo + '-' + d
      },
    },
  }
</script>

<style lang="scss" scoped>
  // Sub-cabecera de bloque (Fechas clave del proyecto / Historial / Instalación).
  .kd-sub {
    padding: 8px 10px 4px; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .03em; color: #3d6b9a;
  }
  .kd-note { margin-left: 8px; font-weight: 400; text-transform: none; color: #9aa4b2; letter-spacing: 0; }

  // Filas etiqueta/valor (mismo patrón que el resto del Resumen).
  .kv { display: flex; align-items: center; border-bottom: 1px solid #eef0f3; min-height: 26px; }
  .kv .lbl {
    width: 240px; flex-shrink: 0; padding: 4px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .02em; color: #5a7896; background: #f6f8fa; align-self: stretch; display: flex; align-items: center;
  }
  .kv .val { flex: 1; display: flex; align-items: center; gap: 2px; padding: 3px 10px; font-size: 11px; color: #2c3a4d; min-width: 0; }
  .kv .val b { font-weight: 600; }
  // Fila sin fuente (manual) en ámbar; fila con fuente real en azul suave (modo Fuente de datos).
  .kv.manual .lbl, .kv.manual .val { background: #fff4d6; }
  .kv.manual .lbl { color: #8a6d00; }
  .kv.sourced .lbl, .kv.sourced .val { background: #eef4fb; }

  // Historial de revisiones. Hasta ~10 filas visibles; con más, la lista se hace scrolleable
  // (altura automática si hay menos de 10).
  .kd-empty { padding: 2px 10px 8px; font-size: 11px; color: #9aa4b2; }
  .kd-rev-list { max-height: 216px; overflow-y: auto; }
  .kd-rev { display: flex; align-items: center; flex-wrap: wrap; gap: 2px 6px; padding: 3px 10px; font-size: 11px; color: #2c3a4d; border-bottom: 1px solid #f2f4f7; }
  .kd-rev-field { font-weight: 700; color: #1e3046; }
  .kd-rev-old { font-weight: 600; }
  .kd-rev-when { color: #9aa4b2; }
  .kd-rev-reason { font-style: italic; color: #6b7787; }
  // Acciones de la revisión (editar / eliminar), empujadas a la derecha.
  .kd-rev-ic { border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 1px 3px; }
  .kd-rev-ic:first-of-type { margin-left: auto; }
  .kd-rev-ic:hover { color: #2f5f8f; }
  .kd-rev-ic.rm:hover { color: #dc2626; }
  // Inputs de edición en línea de una revisión.
  .kd-rev-in { border: .8px solid #e0b52a; border-radius: 4px; padding: 1px 5px; outline: none; background: #fff; font-size: 10px; color: #2c3a4d; }
  .kd-rev-in.date { width: 92px; }
  .kd-rev-in.reason { flex: 1 1 140px; min-width: 100px; }
  .kd-rev-btn { border: 0; border-radius: 4px; padding: 2px 8px; font-size: 10px; cursor: pointer; }
  .kd-rev-btn.save { background: #3d6b9a; color: #fff; font-weight: 600; }
  .kd-rev-btn.save:hover { background: #35608a; }
  .kd-rev-btn.cancel { background: transparent; color: #475569; }
  .kd-rev-btn.cancel:hover { background: #f1f5f9; }
</style>
